import uniqueId from "lodash/uniqueId";
import { cloneDeep } from "../../../../../../utils/cloneDeep";
import { UAE } from "../../../../../../constants";

const toTitleCase = str => {
  if (!str) return str;
  // If it's a single word or ID (no spaces), return as-is to preserve original case
  if (!str.includes(" ")) return str;
  return str
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const initialSiteVisitEntry = {
  signatoryId: "",
  signatoryName: "",
  eidPingType: "",
  eidPingStatus: "",
  eidPingDate: ""
};

export const mapLegacyVisit = v => ({
  id: v.id || uniqueId(),
  signatoryId: v.signatoryId || "",
  signatoryName: v.signatoryName || "",
  eidPingType: v.eidPingType || "",
  eidPingStatus: v.eidPingStatus || "",
  eidPingDate: v.eidPingDate || ""
});

export const buildInitialValues = existingVisits => {
  if (Array.isArray(existingVisits) && existingVisits.length) {
    return {
      siteVisitPostEligibility: existingVisits.map(mapLegacyVisit)
    };
  }
  return { siteVisitPostEligibility: [{ ...cloneDeep(initialSiteVisitEntry), id: uniqueId() }] };
};

export const appendEmptyVisit = (visits = []) => {
  const next = [...visits, { ...cloneDeep(initialSiteVisitEntry), id: uniqueId() }];
  return next;
};

export const getEidEligibleStakeholders = signatories =>
  (signatories || [])
    .filter(st => {
      const shareHoldingPct = Number(st.shareHoldingPercentage || 0);
      const nationalityList = new Set(
        (st.passportDetails || []).map(pd => pd.nationality).filter(Boolean)
      );
      const isLocalShareholderMajor = nationalityList.has(UAE) && shareHoldingPct > 51;
      const isResidenceNonAE = st.residenceCountry && st.residenceCountry !== UAE;
      const isRulingFamily =
        st.memberOfRulingFamily || st.signatoryCompanyInfo?.isMemberofRulingFamily;
      const isPepRuling = st.isPEP === true || st.isPEP === "Yes";
      const isCompanyShareholder = st.isShareholderACompany === true;
      if (isLocalShareholderMajor) return false;
      if (isResidenceNonAE) return false;
      if (isRulingFamily || isPepRuling) return false;
      if (isCompanyShareholder) return false;
      return true;
    })
    .map(st => {
      const name = st.fullName || st.firstName || st.signatoryId;
      return {
        code: st.signatoryId,
        key: st.signatoryId,
        value: st.signatoryId,
        label: toTitleCase(name)
      };
    });

export const buildUniqueStakeholderOptions = (visits, currentIndex, baseOptions) => {
  const currentSelection = visits[currentIndex]?.signatoryId;
  const selectedOtherIds = new Set();
  for (let i = 0; i < visits.length; i += 1) {
    if (i === currentIndex) continue;
    const visit = visits[i];
    if (visit?.signatoryId) {
      selectedOtherIds.add(visit.signatoryId);
    }
  }

  return baseOptions.filter(
    opt => !selectedOtherIds.has(opt.value) || opt.value === currentSelection
  );
};

export const areAllVisitsComplete = (visits, maxVisits) => {
  // Optional logic: user is not forced to fill all possible stakeholder visits.
  // Completion rules:
  // 1. Empty list or all entries blank -> complete.
  // 2. For any entry with signatoryId selected, all dependent fields must be present.
  if (!visits || visits.length === 0) return true;
  return visits.every(visit => {
    if (!visit.signatoryId) return true; // untouched / optional row
    return visit.signatoryName && visit.eidPingType && visit.eidPingStatus && visit.eidPingDate;
  });
};

export default {
  initialSiteVisitEntry,
  mapLegacyVisit,
  buildInitialValues,
  getEidEligibleStakeholders,
  buildUniqueStakeholderOptions,
  appendEmptyVisit,
  areAllVisitsComplete
};
